import {
	type ContextoEvaluarUnirse,
	clasificarCancelacion,
	ERROR_CODES,
	type EtiquetaAccesoExperiencia,
	type ExperienceDetailResponse,
	evaluarUnirse,
	type RedirectTarget,
	type ResultadoEvaluarUnirse,
	safeRedirect,
} from '@diario/common';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { EstadoError } from '../components/ui/estadoError';
import { PopUpAviso } from '../components/ui/popUpAviso';
import { useSesion } from '../features/auth/api/useSesion';
import { useCategories } from '../features/categories/api/useCategories';
import { useActualizarParticipacion } from '../features/experienceJoins/api/useActualizarParticipacion';
import { type AvisarQueNoVoyVariables, useAvisarQueNoVoy } from '../features/experienceJoins/api/useAvisarQueNoVoy';
import { misParticipacionesQueryOptions } from '../features/experienceJoins/api/useMisParticipaciones';
import { useUnirse } from '../features/experienceJoins/api/useUnirse';
import { type AvisarNoVoyProps } from '../features/experienceJoins/components/avisarNoVoy';
import { PantallaCobro } from '../features/experienceJoins/components/pantallaCobro';
import { QueAportas } from '../features/experienceJoins/components/queAportas';
import { SalidasUnirse } from '../features/experienceJoins/components/salidas/salidasUnirse';
import { TablonParticipante } from '../features/experienceJoins/components/tablonParticipante';
import { useExperienceDetail } from '../features/experiences/api/useExperienceDetail';
import { FichaExperiencia } from '../features/experiences/components/ficha/fichaExperiencia';
import { SendaRoles } from '../features/roles/components/sendaRoles';
import { traducirError } from '../lib/errores';
import { ApiError } from '../lib/http';
import { useEnvioUnico } from '../lib/useEnvioUnico';

/**
 * `/unirse/:id` (documento 05b §10.1, p. 5): la ficha pública, oleada 4 (T-4.1). Cablea en una
 * sola pantalla todo lo que construyó la oleada 2/3 de la iteración 05 — la ficha de solo
 * lectura, las cuatro salidas de redirección, el cobro del segundo plan del grumete, "¿qué
 * puedes aportar?" y el tablón con "no voy a poder ir".
 *
 * **Deliberadamente sin `beforeLoad` ni guard de sesión** (RN-05b.11): "es la puerta de entrada
 * del ciclo. Un muro aquí mata la adquisición". `useExperienceDetail` funciona con y sin sesión
 * (`GET /experiences/:id/detail`); es el propio servidor quien decide, según el visor, qué
 * campos redactar.
 *
 * Ningún componente de `features/experienceJoins` ni `features/experiences` conoce rutas: toda
 * la navegación (a `/login`, a `/`) se decide aquí, nunca dentro de un componente presentacional.
 */

/**
 * Búsqueda de la URL de esta ruta (B5/H-06, auditoría funcional 2026-09-10): el `?share=<token>`
 * de un enlace de plan privado, generado por otra tarea. Mismo criterio laxo que
 * `leerBusquedaLogin` (`routes/login.tsx`): no es un contrato con el backend, es una lectura de
 * `location.search`, así que cualquier valor que no sea cadena (incluido `undefined`) se descarta
 * en vez de lanzar. Se reenvía tal cual a `useExperienceDetail` — quien decide si el token es
 * válido es siempre el servidor (`autorizadoParaPlanPrivado`, `experiences.service.ts`), nunca
 * esta lectura.
 */
interface BusquedaUnirse {
	share?: string;
}

const leerBusquedaUnirse = (busqueda: Record<string, unknown>): BusquedaUnirse => ({
	share: typeof busqueda.share === 'string' ? busqueda.share : undefined,
});

export const Route = createFileRoute('/unirse/$id')({ component: PantallaUnirse, validateSearch: leerBusquedaUnirse });

/** Los pasos del asistente de "obtener plaza" que quedan por delante del tablón (documento 05b
 * §10.4/§10.5): el cobro solo si el coste es mayor que cero, la pregunta de aportación solo si
 * el organizador la activó. Ninguno de los dos es una ruta propia — son estados locales de esta
 * pantalla, en el orden en que el documento los describe. */
type PasoObtenerPlaza = 'cobro' | 'aportacion';

/** `Intl.DateTimeFormat` con el idioma activo, mismo criterio que `formatearFecha` de
 * `fichaExperiencia.tsx`: solo esta pantalla necesita formatear `blockedUntil`, así que el
 * formateador vive aquí y no en una utilidad compartida (regla anti-cajón-de-sastre). */
const formatearFechaBloqueo = (iso: string, idioma: string): string =>
	new Intl.DateTimeFormat(idioma, { dateStyle: 'long' }).format(new Date(iso));

/**
 * `EtiquetaAccesoExperiencia` (`accessLabel`) NO viaja en `ExperienceDetailResponse`: ningún
 * visor de `MAPA_VISIBILIDAD_EXPERIENCIA` (`rules/experiences.ts`) la necesita hoy, así que el
 * mapper del servidor nunca la incluye en `GET /experiences/:id/detail` — ni siquiera para
 * `organizadorOAdmin` (esa fila solo viaja por `GET /experiences/:id`, el DTO básico con sesión,
 * otra ruta distinta). Se lee aquí de forma optimista, con `'OFFICIAL'` (el menos restrictivo de
 * los cinco) como valor por defecto cuando no viaja.
 *
 * Desde B5/H-06 (auditoría funcional 2026-09-10) esta ruta SÍ reenvía `?share=` a
 * `useExperienceDetail`, así que ya no es cierto que "si esta ficha se pintó sin sesión, el plan
 * en sí no puede ser privado": un enlace de plan privado con `share` válido pinta esta ficha sin
 * sesión igualmente (`autorizadoParaPlanPrivado`, `experiences.service.ts`, acepta el `shareToken`
 * como alternativa a la sesión). El DTO redactado sigue sin declarar `accessLabel` en ese caso —
 * el mapper del servidor no cambia con esta tarea—, así que el valor por defecto `'OFFICIAL'`
 * sigue siendo el único disponible aquí; queda como hueco conocido (nunca se ha podido alcanzar el
 * árbol de `evaluarUnirse` sin sesión sobre un plan privado real, así que hoy no hay caso de la
 * auditoría que lo demuestre). Leerlo así, en vez de fijar `'OFFICIAL'` sin más, deja que el día
 * que este DTO SÍ traiga `accessLabel`, el árbol de `evaluarUnirse` ya lo respete sin tocar una
 * línea.
 */
const accessLabelDeLaFicha = (experiencia: ExperienceDetailResponse): EtiquetaAccesoExperiencia => {
	const conAccessLabel = experiencia as ExperienceDetailResponse & { accessLabel?: EtiquetaAccesoExperiencia };
	return conAccessLabel.accessLabel ?? 'OFFICIAL';
};

/**
 * El contexto de `evaluarUnirse` (documento 05a §6.2) para cuando `useSesion` ya CONFIRMÓ que no
 * hay sesión (`noHaySesion`, ERR-006) — ver el JSDoc de `manejarApuntarse` para el porqué de
 * decidir aquí en vez de en el servidor. `tieneInvitacionValida` es siempre `false`, y no un
 * valor por defecto cualquiera: sin cuenta no puede existir una fila de
 * "experiencePrivateInvite" que la conceda (se indexa por `userId`). `bloqueoActivo` también es
 * siempre `false`: el bloqueo por ausencias exige historial de participaciones, imposible sin
 * cuenta. El resto de campos (`hayPlaza`, `hayPlazaLibreEnTotal`, `puedeAccederMembersOnly`)
 * nunca deciden el resultado con `haySesion: false` — el árbol corta siempre antes, en el paso
 * (1) o (2) (documento 05a §6.2) — así que valen cualquier valor seguro; se fijan en `false` para
 * que un cambio futuro en el orden del árbol no los deje sin definir por descuido.
 */
const contextoEvaluarUnirseSinSesion = (experiencia: ExperienceDetailResponse): ContextoEvaluarUnirse => ({
	accessLabel: accessLabelDeLaFicha(experiencia),
	bloqueoActivo: false,
	hayPlaza: false,
	hayPlazaLibreEnTotal: false,
	haySesion: false,
	puedeAccederMembersOnly: false,
	tieneInvitacionValida: false,
});

function PantallaUnirse(): React.JSX.Element {
	// Oleada 2 (corrección de rate limiting): namespace `common` sumado al propio (mismo criterio
	// que `centro.tsx`/`tablonExperiencia.tsx`, `useTranslation([propio, 'common'])`) para que `t`
	// tipe `common:errores.ERR-007` sin ensanchar el tipo a cualquier clave — antes de esta tarea
	// el fichero no necesitaba traducir ningún código de error.
	const { t, i18n } = useTranslation(['joins', 'common']);
	const { id } = Route.useParams();
	const { share } = Route.useSearch();
	const navigate = useNavigate();
	// Destino a guardar en `?redirect=` antes de mandar al acceso (pathname + query + hash, mismo
	// criterio que `_autenticado.tsx`/`crear-experiencia.tsx`): así, al volver de `/login`, se
	// entra directo aquí en vez de a la portada. Ya incluye `?share=` cuando llegó en la URL,
	// porque `location.href` conserva la búsqueda entera.
	const destino = useLocation({ select: location => location.href });

	const detalle = useExperienceDetail(id, share);
	// `noHaySesion` (ver JSDoc de `manejarApuntarse`): la única de las tres señales de `useSesion`
	// que autoriza a decidir en el cliente sin llamar a `/join` — "cargando todavía" y "no se pudo
	// confirmar" siguen preguntando al servidor, igual que antes de esta tarea.
	const { noHaySesion, sesion } = useSesion();
	// El suelo de la cascada de portada (documento 03c, `resolverPortada`): el DTO redactado trae
	// `categoryId` pero no el `colorHex`/`emoji` del catálogo — se resuelven aquí, con el mismo
	// catálogo cerrado (seis filas) que ya usa el paso 1 del asistente.
	const categorias = useCategories();

	const unirse = useUnirse();
	const avisar = useAvisarQueNoVoy();
	const actualizarParticipacion = useActualizarParticipacion();
	// Cerrojo de "me apunto" (B3/H-07, ver el JSDoc de `manejarApuntarse` más abajo). Declarado
	// aquí, junto al resto de hooks, y no más abajo: esta función tiene early returns (estados de
	// carga/error de `detalle`) antes de `manejarApuntarse`, y las reglas de los Hooks no permiten
	// llamar a uno después de un `return` condicional.
	const ejecutar = useEnvioUnico();

	const [pasoIndice, setPasoIndice] = useState(0);
	const [mostrarSendaRoles, setMostrarSendaRoles] = useState(false);
	// P-09 (respuestas del cliente 2026-09-17): "Cómo pedir la membresía" no tiene pantalla propia
	// todavía — se queda en esta misma pantalla y enseña el aviso honesto
	// `salidas.puertaMembresia.avisoProximamente` en vez de caer, como las otras cuatro salidas sin
	// destino, en la portada. Mismo criterio que `mostrarSendaRoles`: una bandera local, no una ruta.
	const [mostrarAvisoMembresia, setMostrarAvisoMembresia] = useState(false);
	// P-16 (respuestas del cliente 2026-09-17): abre/cierra la confirmación de salir de la lista de
	// espera (`PopUpAviso`, mismo patrón que `AvisarNoVoy`) — la mutación es la misma `avisar`
	// (`useAvisarQueNoVoy`) que ya usa el tablón para "no voy a poder ir": salir de la lista de
	// espera es la misma llamada a `DELETE /experiences/:id/join`, solo que antes de tener plaza.
	const [avisoSalirListaAbierto, setAvisoSalirListaAbierto] = useState(false);
	// CA-05a.2/RN-05a.2: la decisión resuelta en el cliente cuando `noHaySesion` está confirmado
	// (ver `manejarApuntarse`) — nunca viaja por `unirse.data`, que es la caché de la mutación al
	// servidor, aquí nunca invocada en ese caso. Solo puede salir `outcome: 'redirect'` del árbol
	// con `haySesion: false` (documento 05a §6.2: los pasos (3)/(4) exigen sesión antes de
	// alcanzarse), así que no hace falta modelar aquí `join`/`waitlist`/`blocked`.
	const [decisionSinSesion, setDecisionSinSesion] = useState<ResultadoEvaluarUnirse | undefined>(undefined);

	// "¿Qué puedes aportar?" (documento 05b §10.5) escribe en la participación YA creada por
	// `useUnirse` (`experienceJoin.aventureroSubrole`), así que necesita el `id` de esa fila —
	// `joinDecisionResponseSchema` no lo trae (no es un contrato de creación). Se busca en el
	// listado de participaciones propias, que `useUnirse` ya invalida al tener éxito; solo se
	// activa cuando de verdad hace falta (coste ya resuelto en `join` y la experiencia pregunta),
	// para no lanzar `GET /experience-joins` en cualquier otro desenlace.
	const misParticipaciones = useQuery({
		...misParticipacionesQueryOptions(),
		enabled: sesion !== undefined && unirse.data?.outcome === 'join' && detalle.data?.askContribution === true,
	});

	// Estados de pantalla (documento 05b §10.1, tabla «Estados»): cargando con esqueleto —nunca
	// datos de ejemplo mientras carga. H-12 (residual): un único radio de esqueleto (`rounded-md`)
	// en todo el sistema — `EstadoError variante='carga'` ya lo pinta así, con el mismo
	// `role='status'`/`aria-live='polite'` que tenía este bloque antes de esta tarea.
	if (detalle.isPending) {
		return <EstadoError mensaje={t('estados.cargando')} variante='carga' />;
	}

	if (detalle.isError) {
		// CA-05b.16 / RN-7 de la iteración 02: un 404 (no existe) y un 403 (existe pero sin
		// acceso) producen el MISMO mensaje neutro — distinguirlos delataría qué existe.
		// Cualquier otro código (red, 500, validación…) es un fallo distinto: reintentable, sin
		// perder el sitio en la página.
		const codigo = detalle.error instanceof ApiError ? detalle.error.code : undefined;
		if (codigo === ERROR_CODES.notFound || codigo === ERROR_CODES.forbidden) {
			return <EstadoError mensaje={t('estados.noEncontrada')} salida={{ etiqueta: t('estados.salida'), to: '/' }} />;
		}
		// Oleada 2 (corrección de rate limiting): un 429 no es un fallo reintentable como el
		// resto — pulsar «Reintentar» golpearía de nuevo el mismo endpoint ya limitado, así que
		// esta rama nunca ofrece `reintentar` a `EstadoError` (la prop queda `undefined`, sin
		// botón). El mensaje sale de `traducirError` (`lib/errores.ts`, oleada 1) sobre la clave
		// `common:errores.ERR-007`, ya existente en los dos idiomas antes de esta tarea.
		if (codigo === ERROR_CODES.rateLimited) {
			return <EstadoError mensaje={t(`common:errores.${traducirError(detalle.error)}`)} variante='error' />;
		}
		return (
			<div className='flex flex-col items-start gap-3'>
				<p aria-live='assertive' role='alert'>
					{t('estados.error')}
				</p>
				<Button onClick={() => void detalle.refetch()} type='button'>
					{t('estados.reintentar')}
				</Button>
			</div>
		);
	}

	const experiencia = detalle.data;
	const categoria = categorias.data?.items.find(item => item.id === experiencia.categoryId);
	const propsCategoria = categoria === undefined ? undefined : { colorHex: categoria.colorHex, emoji: categoria.emoji };

	// "Ya pasó" (documento 05b §10.1): sin campo de estado del ciclo de vida en el DTO redactado
	// (`experienceDetailResponseSchema` no expone `status` — ver informe de la tarea), se deriva
	// de la única fecha disponible: si ya se cerró (fin, o inicio si no hay fin), es recuerdo.
	const fechaReferencia = experiencia.endDate ?? experiencia.startDate;
	const yaPaso = fechaReferencia != null && new Date(fechaReferencia).getTime() < Date.now();

	const startDateComoFecha = experiencia.startDate == null ? null : new Date(experiencia.startDate);
	// RN-05a.1: el margen no se reimplementa aquí — `clasificarCancelacion` vive en
	// `rules/joins.ts` y la consumen por igual navegador y servidor. `'sinFecha'` cuenta como "a
	// tiempo" (CL-7: sin fecha no hay "tarde" posible), igual que `'avisada'`.
	const aTiempo = clasificarCancelacion(startDateComoFecha, new Date()) !== 'tarde';
	const propsAvisar: Omit<AvisarNoVoyProps, 'className'> = {
		aTiempo,
		avisado: avisar.isSuccess,
		enviando: avisar.isPending,
		onConfirmar: motivo => {
			const variables: AvisarQueNoVoyVariables = motivo === undefined ? { id } : { id, motivo };
			avisar.mutate(variables);
		},
	};

	const renderTablon = (): React.JSX.Element => (
		<TablonParticipante
			attendees={experiencia.attendees}
			avisar={propsAvisar}
			capacity={experiencia.capacity}
			confirmedCount={experiencia.confirmedCount}
			howToArrive={experiencia.howToArrive}
			meetingPoint={experiencia.meetingPoint}
			startDate={experiencia.startDate}
			whatsappGroupUrl={experiencia.whatsappGroupUrl}
		/>
	);

	// CL-05b.2 / B3-H-07 (auditoría funcional 2026-09-10): pulsar dos veces "me apunto" no crea
	// nada nuevo. El cerrojo es `useEnvioUnico` (`lib/useEnvioUnico.ts`), no `unirse.isPending`:
	// `isPending` es un valor de closure del render anterior y TanStack notifica con
	// `setTimeout(0)`, así que dos `click()` del mismo tick lo verían `false` los dos y dispararían
	// dos peticiones si el guard fuera ese. `useEnvioUnico` marca su cerrojo de forma síncrona,
	// antes de invocar la acción, así que el segundo `click()` del mismo tick lo encuentra ya
	// cerrado. La propia ficha, además, deja de ofrecer el botón mientras `unirse.isPending` es
	// cierto (ver el `onApuntarse` que se pasa a `FichaExperiencia`, más abajo).
	//
	// CA-05a.2/RN-05a.2 (el defecto que arregla la oleada 4): `POST /experiences/:id/join` vive
	// detrás del guard global de sesión (`SupabaseJwtGuard`, `app.module.ts`) — sin sesión responde
	// 401 (`ERR-006`) antes de llegar siquiera al árbol de `evaluarUnirse`, así que la salida
	// "crear cuenta" (RN-05a.2: "es adquisición, no un muro") es INALCANZABLE a través de la API
	// por construcción. Por eso, con `noHaySesion` ya CONFIRMADO (nunca con "cargando todavía" ni
	// "no se pudo confirmar" — `useSesion` distingue los tres, y solo el primero autoriza a
	// decidir aquí), la decisión se resuelve en el propio cliente con la MISMA función pura que
	// usa el servidor (`evaluarUnirse`, RN-05a.1: "el navegador enseña, el servidor permite, con
	// la misma fuente, así que nunca divergen") — nunca cortocircuitando con un
	// `if (!haySesion) mostrarCrearCuenta`, que se saltaría por delante el corte de plan privado
	// sin invitación (RN-05a.3: "la primera comprobación que salta manda").
	const manejarApuntarse = (): void => {
		ejecutar(async () => {
			setPasoIndice(0);
			if (noHaySesion) {
				setDecisionSinSesion(evaluarUnirse(contextoEvaluarUnirseSinSesion(experiencia)));
				return;
			}
			try {
				await unirse.mutateAsync({ datos: {}, id });
			} catch (error) {
				// Carrera entre el clic y la confirmación de `useSesion` (`GET /me` más lento que el
				// primer render de esta ficha): con "cargando todavía" ya en curso, `noHaySesion` de
				// arriba sigue siendo `false` (RN-05a.2, JSDoc de este bloque) y `POST /join` viaja
				// contra un guard global de sesión que responde 401 (`ERR-006`) antes de llegar al
				// árbol de `evaluarUnirse`. Sin este `catch`, ese 401 se perdía en el propio
				// `useEnvioUnico` (que solo evita el "unhandled rejection") y la pantalla se quedaba
				// exactamente igual — nada visible tras el clic, el defecto que reporta esta tarea.
				// Se resuelve EXACTAMENTE con la misma decisión que si `noHaySesion` hubiera llegado
				// confirmado antes del clic: el servidor acaba de confirmar, con su propio 401, que no
				// hay sesión, así que el resultado no puede ser otro.
				if (error instanceof ApiError && error.code === ERROR_CODES.unauthorized) {
					setDecisionSinSesion(evaluarUnirse(contextoEvaluarUnirseSinSesion(experiencia)));
					return;
				}
				throw error;
			}
		});
	};

	/**
	 * Las cuatro salidas de redirección (documento 05b §10.2). "Crear cuenta" navega de verdad —
	 * a `/login`, ya en modo "crear cuenta" (B7/H-11, auditoría funcional 2026-09-10: antes
	 * aterrizaba en "entrar", obligando a un clic extra en el conmutador) — y guarda el destino.
	 * "Ver los roles" (secundaria de crear cuenta, `SendaRoles`, sin depender de sesión) y "volver"
	 * de recomendaciones (descarta la decisión y vuelve a enseñar la ficha) ya estaban resueltas.
	 *
	 * P-09 (respuestas del cliente 2026-09-17, cinco botones que antes caían todos en la portada):
	 * las dos "ver experiencias OFICIALES" (plan privado, solo miembros) llevan al catálogo público
	 * (`/experiencias`, ya sin exigir sesión) con `search={{ soloOficiales: true }}` — mismo filtro
	 * que ya usa `bienvenida.tsx` para el mismo texto de botón, nunca el catálogo entero para un
	 * botón que promete "oficiales". "Ver el calendario" (recomendaciones, sin nada real que
	 * recomendar todavía) lleva al mismo catálogo pero sin ese filtro — es un calendario completo.
	 * "¿qué es un plan privado?" lleva a `/saber-mas`; "cómo pedir la membresía" se queda en esta
	 * misma pantalla y enseña el aviso honesto `salidas.puertaMembresia.avisoProximamente` en vez
	 * de una pantalla que todavía no existe.
	 */
	const manejarSalidaRedirect = (redirectTo: RedirectTarget, salida: 'principal' | 'secundaria'): void => {
		if (redirectTo === 'createAccount' && salida === 'principal') {
			void navigate({ search: { modo: 'crearCuenta', redirect: safeRedirect(destino) }, to: '/login' });
			return;
		}
		if (redirectTo === 'createAccount' && salida === 'secundaria') {
			setMostrarSendaRoles(true);
			return;
		}
		if (redirectTo === 'recommendations' && salida === 'secundaria') {
			unirse.reset();
			return;
		}
		// P-09 (respuestas del cliente 2026-09-17): las dos "ver experiencias OFICIALES" (plan
		// privado, solo miembros) llevan al catálogo público (ya sin exigir sesión) con el mismo
		// filtro que ya usa `bienvenida.tsx` para el mismo texto de botón (`search={{
		// soloOficiales: true }}`) — nunca el catálogo entero para un botón que promete "oficiales".
		if (
			(redirectTo === 'invitationRequired' && salida === 'principal')
			|| (redirectTo === 'membershipGate' && salida === 'secundaria')
		) {
			void navigate({ search: { soloOficiales: true }, to: '/experiencias' });
			return;
		}
		if (redirectTo === 'invitationRequired' && salida === 'secundaria') {
			void navigate({ to: '/saber-mas' });
			return;
		}
		if (redirectTo === 'membershipGate' && salida === 'principal') {
			setMostrarAvisoMembresia(true);
			return;
		}
		// "Ver el calendario" (recomendaciones, sin nada real que recomendar todavía): el catálogo
		// entero, sin el filtro de "oficiales" — es un calendario completo, no una lista acotada.
		if (redirectTo === 'recommendations' && salida === 'principal') {
			void navigate({ search: { soloOficiales: false }, to: '/experiencias' });
			return;
		}
		void navigate({ to: '/' });
	};

	/**
	 * Pinta una de las cuatro salidas de redirección (`SalidasUnirse`): la comparten `unirse.data`
	 * (decisión del servidor, con sesión) y `decisionSinSesion` (decisión resuelta en el cliente,
	 * ver `manejarApuntarse`) — la pantalla es la misma, decida quien decida.
	 */
	const renderRedirect = (redirectTo: RedirectTarget): React.JSX.Element => (
		<div className='flex flex-col gap-6'>
			<SalidasUnirse
				onSalidaPrincipal={() => manejarSalidaRedirect(redirectTo, 'principal')}
				onSalidaSecundaria={() => manejarSalidaRedirect(redirectTo, 'secundaria')}
				redirectTo={redirectTo}
			/>
			{mostrarSendaRoles ? (
				<div className='mx-auto w-full max-w-2xl'>
					<SendaRoles />
				</div>
			) : null}
			{mostrarAvisoMembresia ? (
				<Alert className='mx-auto w-full max-w-md' variante='informativo'>
					{t('salidas.puertaMembresia.avisoProximamente')}
				</Alert>
			) : null}
		</div>
	);

	// La ruta despacha según `outcome` (documento 05a §6.7; documento 05b §10.1/§10.2; documento
	// 06a §7.1/§5.1 para `requested`, la quinta salida que introduce esa iteración). Una decisión
	// de ESTA sesión (recién llegada de pulsar "me apunto") manda sobre cualquier otra lectura del
	// DTO, para que el asistente de cobro/aportación no se salte pasos aunque la invalidación de
	// `useUnirse` ya haya puesto `viewerJoinStatus` al día.
	if (unirse.data !== undefined) {
		const resultado = unirse.data;

		if (resultado.outcome === 'redirect') {
			const redirectTo = resultado.redirectTo;
			if (redirectTo !== undefined) {
				return renderRedirect(redirectTo);
			}
		}

		if (resultado.outcome === 'blocked') {
			// CA-05b.14: se explica cuánto queda y por qué, sin culpabilizar, con la regla escrita.
			// Ámbar (`aviso`), no rojo: es una pausa temporal, no una falta que castigar.
			return (
				<Alert variante='aviso'>
					<p className='font-display text-lg'>{t('bloqueo.titulo')}</p>
					<p>{t('bloqueo.texto')}</p>
					<p>{t('bloqueo.regla')}</p>
					{resultado.blockedUntil === undefined ? null : (
						<p>{t('bloqueo.hasta', { fecha: formatearFechaBloqueo(resultado.blockedUntil, i18n.language) })}</p>
					)}
				</Alert>
			);
		}

		if (resultado.outcome === 'waitlist') {
			// P-16 (respuestas del cliente 2026-09-17): antes este aviso terminaba en la posición, sin
			// forma de salir — "la cifra de lista de espera miente, y quien organiza toma decisiones
			// con ella". El servidor ya soporta `DELETE /experiences/:id/join`; `avisar` es la MISMA
			// mutación (`useAvisarQueNoVoy`) que ya usa el tablón para "no voy a poder ir", reutilizada
			// tal cual — las dos pantallas nunca se pintan a la vez, así que su estado no choca.
			if (avisar.isSuccess) {
				return (
					<Alert variante='exito'>
						<p>{t('listaDeEspera.exito')}</p>
					</Alert>
				);
			}
			// CL-05b.1: la última plaza ocupada con la ficha abierta se explica, no es un error.
			// D-32: la posición es aproximada, nunca exacta — solo viaja en esta respuesta.
			return (
				<Alert variante='aviso'>
					<p className='font-display text-lg'>{t('aforo.enListaDeEspera')}</p>
					<p>{t(`motivos.${resultado.reason}`)}</p>
					{resultado.waitlistPosition === undefined ? null : (
						<p>{t('aforo.posicionAproximada', { posicion: resultado.waitlistPosition })}</p>
					)}
					<Button
						aria-haspopup='dialog'
						className='min-w-11'
						onClick={() => setAvisoSalirListaAbierto(true)}
						variant='secondary'
					>
						{t('listaDeEspera.accion')}
					</Button>
					<PopUpAviso
						actionLabel={avisar.isPending ? t('accion.enviando') : t('listaDeEspera.confirmar')}
						actionVariant='dark'
						image={null}
						onAction={avisar.isPending ? undefined : () => avisar.mutate({ id })}
						onClose={avisar.isPending ? undefined : () => setAvisoSalirListaAbierto(false)}
						open={avisoSalirListaAbierto}
						secondaryLabel={t('listaDeEspera.volver')}
						title={t('listaDeEspera.titulo')}
					/>
				</Alert>
			);
		}

		if (resultado.outcome === 'requested') {
			// CA-06a.6/RN-2 del documento 06a (§7.1, §5.1): en una experiencia oficial, obtener
			// hueco ya no da plaza directamente — deja una solicitud pendiente de que quien
			// organiza la acepte. La plaza (y, si aplica, el ascenso a grumete) llegan al aceptar,
			// no al pedirla. `resultado.reason` sigue siendo `hayPlaza` (el motor de reglas no
			// conoce solicitudes, y el contrato no suma un motivo nuevo — decisión cerrada), así
			// que esta rama NUNCA pinta `motivos.${resultado.reason}`: sería falso ("hay plaza
			// para ti" cuando justo no la hay todavía). Usa un texto propio. Nada de punto de
			// encuentro, hora ni enlace del grupo: el visor `apuntado` sigue exigiendo
			// `CONFIRMED`/`ATTENDED`, así que el servidor no manda esos campos y esta pantalla no
			// debe insinuar que existan.
			return (
				<Alert variante='informativo'>
					<p className='font-display text-lg'>{t('solicitud.titulo')}</p>
					<p>{t('solicitud.texto')}</p>
					<p>{t('solicitud.cuandoTeAcepten')}</p>
				</Alert>
			);
		}

		if (resultado.outcome === 'join') {
			const pasos: PasoObtenerPlaza[] = [];
			if (resultado.cost.cents > 0) {
				pasos.push('cobro');
			}
			if (experiencia.askContribution === true) {
				pasos.push('aportacion');
			}
			const pasoActual = pasos[pasoIndice];
			const avanzarPaso = (): void => setPasoIndice(indice => indice + 1);

			if (pasoActual === 'cobro') {
				return (
					<PantallaCobro
						condicionesPropias={experiencia.condicionesPropias}
						condicionesPropiasEnlace={experiencia.condicionesPropiasEnlace}
						cost={resultado.cost}
						experienceId={id}
						onConfirmar={avanzarPaso}
						organizerFullName={experiencia.organizerFullName}
					/>
				);
			}
			if (pasoActual === 'aportacion') {
				// El servidor ya escribió la fila al obtener plaza; esto solo la completa con lo
				// que la persona elige aquí (documento 05b §10.5).
				const miParticipacion = misParticipaciones.data?.items.find(
					item => item.experienceId === id && item.status !== 'CANCELLED' && item.status !== 'REJECTED',
				);
				return (
					<QueAportas
						activo={true}
						onElegir={subrole => {
							if (miParticipacion !== undefined) {
								actualizarParticipacion.mutate({ datos: { aventureroSubrole: subrole }, id: miParticipacion.id });
							}
							avanzarPaso();
						}}
						onOmitir={avanzarPaso}
					/>
				);
			}
			return renderTablon();
		}
	}

	// CA-05a.2/RN-05a.2/RN-05a.3: la decisión resuelta en el cliente cuando no hay sesión (ver
	// `manejarApuntarse`) — siempre `redirect` (nunca `join`/`waitlist`/`blocked`, ver el JSDoc de
	// `decisionSinSesion`), a `crearCuenta` salvo que el plan resultara privado sin invitación,
	// en cuyo caso el árbol de `evaluarUnirse` ya sale por `invitationRequired` primero.
	if (
		decisionSinSesion !== undefined
		&& decisionSinSesion.outcome === 'redirect'
		&& decisionSinSesion.redirectTo !== undefined
	) {
		return renderRedirect(decisionSinSesion.redirectTo);
	}

	// Quien vuelve a la dirección con plaza ve su tablón, no la ficha pelada.
	if (experiencia.viewerJoinStatus === 'CONFIRMED' || experiencia.viewerJoinStatus === 'ATTENDED') {
		return renderTablon();
	}

	if (yaPaso) {
		return (
			<div className='flex flex-col gap-6'>
				<Alert variante='informativo'>{t('estados.yaPaso')}</Alert>
				<FichaExperiencia categoria={propsCategoria} experiencia={experiencia} haySesion={sesion !== undefined} />
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-4'>
			<FichaExperiencia
				categoria={propsCategoria}
				experiencia={experiencia}
				haySesion={sesion !== undefined}
				onApuntarse={unirse.isPending ? undefined : manejarApuntarse}
			/>
			{unirse.isPending ? (
				<p aria-live='polite' role='status'>
					{t('accion.enviando')}
				</p>
			) : null}
		</div>
	);
}
